import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import { Gamepad2, TrendingUp, CheckCircle, Sparkles, ArrowRight, Target, Library, Book, BookOpen } from 'lucide-react';

const InteractiveBooks = () => {
  const navigate = useNavigate();
  const books = [
    {
      title: 'CIRE Interactive Quick Study Guide',
      exam: 'CIRE',
      gradient: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
      color: '#3b82f6',
      bgColor: '#eff6ff',
      elements: 9,
      price: '34.99',
      originalPrice: '99.99',
      link: '/books/cire-interactive',
      features: ['9 Interactive Elements', 'Full Mock Exam', 'Instant Feedback', 'Lifetime Access'],
      description: 'Master the Canadian Investment Regulatory Examination with our revolutionary interactive study system. 9 comprehensive elements with visual learning tools, practice questions, and real-time feedback.',
      icon: null
    },
    {
      title: 'RSE Interactive Quick Study Guide',
      exam: 'RSE',
      gradient: 'linear-gradient(135deg, #22d3ee 0%, #0891b2 100%)',
      color: '#22d3ee',
      bgColor: '#ecfeff',
      elements: 9,
      price: '49.99',
      originalPrice: '129.99',
      link: '/books/rse-interactive',
      features: ['9 Interactive Elements', 'Mock Exam with Diagnostics', 'Reading Notes', 'Visual Learning'],
      description: 'Master all RSE topics including capital markets, economics, equities, derivatives, and investment funds.',
      icon: null
    },
    {
      title: 'Derivatives Interactive Quick Study Guide',
      exam: 'Derivatives',
      gradient: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
      color: '#8b5cf6',
      bgColor: '#f5f3ff',
      elements: 8,
      price: '49.99',
      originalPrice: '129.99',
      link: '/books/derivatives-interactive',
      features: ['8 Interactive Elements', 'Mock Exam with Diagnostics', 'Options & Futures', 'Risk Management'],
      description: 'Master options, futures, swaps, and risk management with comprehensive interactive modules.',
      icon: null
    },
    {
      title: 'ISE Interactive Quick Study Guide',
      exam: 'ISE',
      gradient: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
      color: '#f97316',
      bgColor: '#fff7ed',
      elements: 7,
      price: '49.99',
      originalPrice: '129.99',
      link: '/books/ise-interactive',
      features: ['7 Interactive Elements', 'Mock Exam with Diagnostics', 'Institutional Focus', 'Visual Learning'],
      description: 'Master CIRO Institutional Securities Exam with comprehensive coverage of institutional capital markets, client relationships, and portfolio management.',
      icon: null
    },
    {
      title: 'Traders Licensing Exam Course',
      exam: 'Traders',
      gradient: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
      color: '#0d9488',
      bgColor: '#ccfbf1',
      elements: 10,
      price: '199.00',
      originalPrice: null,
      link: '/traders-exam',
      features: ['10 Interactive Elements', 'Video Lectures & Podcast', 'Practice Questions', 'Full Mock Exams'],
      description: 'Complete preparation for the CIRO Traders Licensing Examination covering trading rules, marketplaces, trade execution, and compliance.',
      icon: null,
      comingSoon: true
    }
  ];

  return (
    <>
      <SEO 
        title="Interactive Quick Study Guides | CIRE, RSE & Derivatives | Vitta Finance"
        description="Master CIRO exams with Canada's most interactive study experience. Comprehensive elements with reading notes, games, flashcards, and quizzes. 62% off April special."
        keywords="CIRO interactive study guide, CIRE interactive book, RSE interactive guide, Derivatives interactive study, interactive exam prep, CIRO games, CIRO flashcards"
      />

      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)'
      }}>
        {/* Hero Section */}
        <section style={{
          padding: '120px 24px 60px',
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: -100,
            left: -100,
            width: 400,
            height: 400,
            background: 'radial-gradient(circle, rgba(59, 130, 246, 0.15) 0%, transparent 70%)',
            borderRadius: '50%'
          }} />
          <div style={{
            position: 'absolute',
            bottom: -150,
            right: -150,
            width: 500,
            height: 500,
            background: 'radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 70%)',
            borderRadius: '50%'
          }} />

          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            position: 'relative',
            zIndex: 1
          }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '100px',
              padding: '10px 24px',
              marginBottom: '32px'
            }}>
              <Sparkles size={16} color="#60a5fa" />
              <span style={{ color: '#60a5fa', fontSize: '15px', fontWeight: '600' }}>
                April 2025 Special - Up to 62% Off
              </span>
            </div>

            <h1 style={{
              fontSize: 'clamp(2.5rem, 5vw, 4rem)',
              fontWeight: '900',
              color: 'white',
              marginBottom: '24px',
              lineHeight: '1.1'
            }}>
              Interactive Quick Study Guides<br />
              <span style={{
                background: 'linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                That Actually Work
              </span>
            </h1>

            <p style={{
              fontSize: '1.25rem',
              color: '#cbd5e1',
              maxWidth: '700px',
              marginBottom: '40px',
              lineHeight: '1.7'
            }}>
              Master CIRO exams with engaging interactive study experience. 
              Interactive elements featuring reading notes, games, flashcards, and practice quizzes.
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '24px',
              maxWidth: '800px'
            }}>
              {[
                { icon: <Gamepad2 size={24} />, label: 'Interactive Games', desc: 'Engaging learning' },
                { icon: <TrendingUp size={24} />, label: 'Progress Tracking', desc: 'Know your readiness' },
                { icon: <CheckCircle size={24} />, label: 'Mock Exams', desc: 'Full diagnostics' }
              ].map((item, idx) => (
                <div key={idx} style={{
                  background: 'rgba(255,255,255,0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '12px',
                  padding: '20px',
                  textAlign: 'center'
                }}>
                  <div style={{ color: '#60a5fa', marginBottom: '12px' }}>{item.icon}</div>
                  <div style={{ fontSize: '1rem', fontWeight: '700', color: 'white', marginBottom: '4px' }}>
                    {item.label}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#94a3b8' }}>
                    {item.desc}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Books Grid */}
        <section style={{
          padding: '80px 24px',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          <div style={{
            textAlign: 'center',
            marginBottom: '48px'
          }}>
            <div style={{
              display: 'inline-block',
              background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
              color: 'white',
              padding: '8px 20px',
              borderRadius: '100px',
              fontSize: '14px',
              fontWeight: '700',
              marginBottom: '16px',
              letterSpacing: '0.5px'
            }}>
              CIRE EXAM PREP
            </div>
            <h2 style={{
              fontSize: 'clamp(1.75rem, 4vw, 2.25rem)',
              fontWeight: '900',
              color: '#0f172a',
              marginBottom: '12px'
            }}>
              Complete CIRE Study Package
            </h2>
            <p style={{
              fontSize: '1.05rem',
              color: '#64748b',
              maxWidth: '600px',
              margin: '0 auto 32px'
            }}>
              Interactive guide, mock exams, and element-wise practice - everything you need to pass CIRE
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '28px',
            marginBottom: '60px'
          }}>
            {/* CIRE Interactive Quick Study Guide */}
            {books.filter(book => book.exam === 'CIRE').map((book, idx) => (
              <div key={idx} style={{
                background: 'white',
                borderRadius: '20px',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                transition: 'all 0.3s',
                border: '1px solid #e2e8f0'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.15)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
              }}>
                {/* Header */}
                <div style={{
                  background: book.gradient,
                  padding: '32px 24px',
                  textAlign: 'center',
                  position: 'relative'
                }}>
                  <div style={{ width: '80px', height: '80px', margin: '0 auto 16px' }}>{book.icon}</div>
                  <h3 style={{
                    fontSize: '1.5rem',
                    fontWeight: '800',
                    color: 'white',
                    marginBottom: '8px'
                  }}>
                    {book.exam}
                  </h3>
                  <p style={{
                    fontSize: '0.875rem',
                    color: 'rgba(255,255,255,0.9)',
                    margin: 0
                  }}>
                    Interactive Quick Study
                  </p>
                </div>

                {/* Content */}
                <div style={{ padding: '32px 24px' }}>
                  <p style={{
                    fontSize: '0.95rem',
                    color: '#475569',
                    lineHeight: '1.6',
                    marginBottom: '24px'
                  }}>
                    {book.description}
                  </p>

                  {/* Features */}
                  <div style={{ marginBottom: '24px' }}>
                    {book.features.map((feature, fIdx) => (
                      <div key={fIdx} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '12px'
                      }}>
                        <CheckCircle size={18} color={book.color} />
                        <span style={{ fontSize: '0.9rem', color: '#334155' }}>{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Pricing */}
                  <div style={{
                    background: book.bgColor,
                    borderRadius: '12px',
                    padding: '20px',
                    marginBottom: '24px',
                    textAlign: 'center'
                  }}>
                    <div style={{
                      fontSize: '0.875rem',
                      color: '#64748b',
                      textDecoration: 'line-through',
                      marginBottom: '4px'
                    }}>
                      ${book.originalPrice} CAD
                    </div>
                    <div style={{
                      fontSize: '2.5rem',
                      fontWeight: '900',
                      color: book.color,
                      marginBottom: '4px'
                    }}>
                      ${book.price}
                    </div>
                    <div style={{
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: book.color
                    }}>
                      April Special
                    </div>
                  </div>

                  {/* CTA Button */}
                  <Link
                    to={book.link}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      width: '100%',
                      padding: '16px',
                      background: book.gradient,
                      color: 'white',
                      borderRadius: '12px',
                      fontSize: '1rem',
                      fontWeight: '700',
                      textDecoration: 'none',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                    onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    Explore Interactive Guide
                    <ArrowRight size={20} />
                  </Link>
                </div>
              </div>
            ))}

            {/* CIRE Mock Exam Pack Tile */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(212,175,55,0.1) 0%, rgba(59,130,246,0.1) 100%)',
              borderRadius: '20px',
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(212,175,55,0.15)',
              transition: 'all 0.3s',
              border: '2px solid rgba(212,175,55,0.3)',
              position: 'relative'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-8px)';
              e.currentTarget.style.boxShadow = '0 12px 40px rgba(212,175,55,0.25)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(212,175,55,0.15)';
            }}>
              {/* Badge */}
              <div style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                background: 'rgba(212,175,55,0.2)',
                border: '1px solid rgba(212,175,55,0.5)',
                borderRadius: '20px',
                padding: '6px 14px',
                fontSize: '0.75rem',
                color: '#B8860B',
                fontWeight: '700',
                letterSpacing: '0.5px'
              }}>
                NEW
              </div>

              {/* Header */}
              <div style={{
                background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)',
                padding: '32px 24px',
                textAlign: 'center',
                position: 'relative'
              }}>
                <div style={{ width: '80px', height: '80px', margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Target size={56} color="white" />
                </div>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '800',
                  color: 'white',
                  marginBottom: '8px'
                }}>
                  CIRE Mock Exam Pack
                </h3>
                <p style={{
                  fontSize: '0.875rem',
                  color: 'rgba(255,255,255,0.9)',
                  margin: 0
                }}>
                  5 Full-Length Practice Exams
                </p>
              </div>

              {/* Content */}
              <div style={{ padding: '32px 24px' }}>
                <p style={{
                  fontSize: '0.95rem',
                  color: '#475569',
                  lineHeight: '1.6',
                  marginBottom: '24px'
                }}>
                  Test your CIRE readiness with 5 complete mock exams. 550 questions total with comprehensive rationales for every question and answer.
                </p>

                {/* Features */}
                <div style={{ marginBottom: '24px' }}>
                  {['550 Exam-Style Questions', 'Detailed Rationales', 'Practice & Exam Modes', 'Performance Tracking', 'Lifetime Access'].map((feature, fIdx) => (
                    <div key={fIdx} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '12px'
                    }}>
                      <CheckCircle size={18} color="#D4AF37" />
                      <span style={{ fontSize: '0.9rem', color: '#334155' }}>{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Pricing */}
                <div style={{
                  background: 'rgba(212,175,55,0.1)',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '24px',
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontSize: '2.5rem',
                    fontWeight: '900',
                    color: '#B8860B',
                    marginBottom: '4px'
                  }}>
                    $49.99
                  </div>
                  <div style={{
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: '#B8860B'
                  }}>
                    One-Time Payment
                  </div>
                </div>

                {/* CTA Button */}
                <Link
                  to="/books/cire-mock-exam-pack"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    width: '100%',
                    padding: '16px',
                    background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)',
                    color: 'white',
                    borderRadius: '12px',
                    fontSize: '1rem',
                    fontWeight: '700',
                    textDecoration: 'none',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                  onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  View Mock Exam Pack
                  <ArrowRight size={20} />
                </Link>
              </div>
            </div>

            {/* CIRE Element-wise Question Bank Tile */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(139,92,246,0.1) 0%, rgba(59,130,246,0.1) 100%)',
              borderRadius: '20px',
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(139,92,246,0.15)',
              transition: 'all 0.3s',
              border: '2px solid rgba(139,92,246,0.3)',
              position: 'relative'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-8px)';
              e.currentTarget.style.boxShadow = '0 12px 40px rgba(139,92,246,0.25)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(139,92,246,0.15)';
            }}>
              {/* Badge */}
              <div style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                background: 'rgba(139,92,246,0.2)',
                border: '1px solid rgba(139,92,246,0.5)',
                borderRadius: '20px',
                padding: '6px 14px',
                fontSize: '0.75rem',
                color: '#8b5cf6',
                fontWeight: '700',
                letterSpacing: '0.5px'
              }}>
                NEW
              </div>

              {/* Header */}
              <div style={{
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                padding: '32px 24px',
                textAlign: 'center',
                position: 'relative'
              }}>
                <div style={{ width: '80px', height: '80px', margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Library size={56} color="white" />
                </div>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '800',
                  color: 'white',
                  marginBottom: '8px'
                }}>
                  CIRE Element-Wise Question Bank
                </h3>
                <p style={{
                  fontSize: '0.875rem',
                  color: 'rgba(255,255,255,0.9)',
                  margin: 0
                }}>
                  9 Targeted Element Practice Banks
                </p>
              </div>

              {/* Content */}
              <div style={{ padding: '32px 24px' }}>
                <p style={{
                  fontSize: '0.95rem',
                  color: '#475569',
                  lineHeight: '1.6',
                  marginBottom: '24px'
                }}>
                  Master each CIRE element with focused practice questions. Detailed rationales for every question to strengthen weak areas element by element.
                </p>

                {/* Features */}
                <div style={{ marginBottom: '24px' }}>
                  {['9 Element Question Banks', 'Detailed Rationales', 'Practice & Test Modes', 'Performance Tracking', 'Lifetime Access'].map((feature, fIdx) => (
                    <div key={fIdx} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '12px'
                    }}>
                      <CheckCircle size={18} color="#8b5cf6" />
                      <span style={{ fontSize: '0.9rem', color: '#334155' }}>{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Pricing */}
                <div style={{
                  background: 'rgba(139,92,246,0.1)',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '24px',
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontSize: '2.5rem',
                    fontWeight: '900',
                    color: '#7c3aed',
                    marginBottom: '4px'
                  }}>
                    $49.99
                  </div>
                  <div style={{
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: '#7c3aed'
                  }}>
                    One-Time Payment
                  </div>
                </div>

                {/* CTA Button */}
                <Link
                  to="/books/cire-element-bank"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    width: '100%',
                    padding: '16px',
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    color: 'white',
                    borderRadius: '12px',
                    fontSize: '1rem',
                    fontWeight: '700',
                    textDecoration: 'none',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                  onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  View Element Question Bank
                  <ArrowRight size={20} />
                </Link>
              </div>
            </div>
          </div>

          {/* RSE Products Section */}
          <div style={{ marginBottom: '80px' }}>
            <div style={{
              textAlign: 'center',
              marginBottom: '48px'
            }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                padding: '8px 20px',
                borderRadius: '100px',
                marginBottom: '16px'
              }}>
                <span style={{ color: 'white', fontSize: '14px', fontWeight: '700', letterSpacing: '0.5px' }}>
                RSE EXAM PREP
                </span>
              </div>
              <h2 style={{
                fontSize: 'clamp(1.75rem, 4vw, 2.25rem)',
                fontWeight: '900',
                color: '#0f172a',
                marginBottom: '12px'
              }}>
                RSE Study Resources
              </h2>
              <p style={{
                fontSize: '1.05rem',
                color: '#64748b',
                maxWidth: '700px',
                margin: '0 auto 32px'
              }}>
                Master the Retail Securities Exam with our interactive guide and practice exams
              </p>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
              gap: '28px',
              marginBottom: '32px'
            }}>
              {/* RSE Interactive Quick Study Guide */}
              <div style={{
                background: 'white',
                borderRadius: '20px',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                transition: 'all 0.3s',
                border: '1px solid #e2e8f0'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 12px 40px rgba(139,92,246,0.2)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
              }}>
                {/* Header */}
                <div style={{
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  padding: '32px 24px',
                  textAlign: 'center',
                  position: 'relative'
                }}>
                  <div style={{ width: '80px', height: '80px', margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Book size={56} color="white" />
                </div>
                  <h3 style={{
                    fontSize: '1.5rem',
                    fontWeight: '800',
                    color: 'white',
                    marginBottom: '8px'
                  }}>
                    Interactive Quick Study
                  </h3>
                  <p style={{
                    fontSize: '0.875rem',
                    color: 'rgba(255,255,255,0.9)',
                    margin: 0
                  }}>
                    9 Interactive Elements
                  </p>
                </div>

                {/* Content */}
                <div style={{ padding: '28px 24px' }}>
                  <p style={{
                    fontSize: '0.95rem',
                    color: '#475569',
                    lineHeight: '1.6',
                    marginBottom: '20px',
                    minHeight: '72px'
                  }}>
                    Master all 9 RSE elements with interactive games, flashcards, quizzes, and comprehensive reading notes.
                  </p>

                  {/* Features */}
                  <div style={{ marginBottom: '20px' }}>
                    {['9 Interactive Elements', 'Full Mock Exam', 'Instant Feedback', 'Lifetime Access'].map((feature, fIdx) => (
                      <div key={fIdx} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        marginBottom: '10px'
                      }}>
                        <CheckCircle size={16} color="#8b5cf6" />
                        <span style={{ fontSize: '0.875rem', color: '#334155' }}>{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Pricing */}
                  <div style={{
                    background: 'rgba(139,92,246,0.08)',
                    borderRadius: '12px',
                    padding: '16px',
                    marginBottom: '20px',
                    textAlign: 'center'
                  }}>
                    <div style={{
                      fontSize: '0.8rem',
                      color: '#64748b',
                      textDecoration: 'line-through',
                      marginBottom: '2px'
                    }}>
                      $129.99 CAD
                    </div>
                    <div style={{
                      fontSize: '2.25rem',
                      fontWeight: '900',
                      color: '#8b5cf6',
                      marginBottom: '2px',
                      lineHeight: 1
                    }}>
                      $49.99
                    </div>
                    <div style={{
                      fontSize: '0.8rem',
                      fontWeight: '600',
                      color: '#8b5cf6'
                    }}>
                      62% Off April Special
                    </div>
                  </div>

                  {/* CTA Button */}
                  <Link
                    to="/books/rse-interactive"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      width: '100%',
                      padding: '14px',
                      background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                      color: 'white',
                      borderRadius: '10px',
                      fontSize: '0.95rem',
                      fontWeight: '700',
                      textDecoration: 'none',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                    onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    View Interactive Guide
                    <ArrowRight size={18} />
                  </Link>
                </div>
              </div>

              {/* RSE Mock Exam Pack */}
              <div style={{
                background: 'white',
                borderRadius: '20px',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(212,175,55,0.12)',
                transition: 'all 0.3s',
                border: '2px solid rgba(212,175,55,0.3)',
                position: 'relative'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 12px 40px rgba(212,175,55,0.25)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(212,175,55,0.12)';
              }}>
                {/* Badge */}
                <div style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  background: 'rgba(212,175,55,0.2)',
                  border: '1px solid rgba(212,175,55,0.5)',
                  borderRadius: '20px',
                  padding: '6px 12px',
                  fontSize: '0.7rem',
                  color: '#B8860B',
                  fontWeight: '700',
                  letterSpacing: '0.5px',
                  zIndex: 1
                }}>
                  NEW
                </div>

                {/* Header */}
                <div style={{
                  background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)',
                  padding: '32px 24px',
                  textAlign: 'center',
                  position: 'relative'
                }}>
                  <div style={{ width: '80px', height: '80px', margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Target size={56} color="white" />
                  </div>
                  <h3 style={{
                    fontSize: '1.5rem',
                    fontWeight: '800',
                    color: 'white',
                    marginBottom: '8px'
                  }}>
                    Mock Exam Pack
                  </h3>
                  <p style={{
                    fontSize: '0.875rem',
                    color: 'rgba(255,255,255,0.9)',
                    margin: 0
                  }}>
                    5 Full-Length Exams
                  </p>
                </div>

                {/* Content */}
                <div style={{ padding: '28px 24px' }}>
                  <p style={{
                    fontSize: '0.95rem',
                    color: '#475569',
                    lineHeight: '1.6',
                    marginBottom: '20px',
                    minHeight: '72px'
                  }}>
                    Test your readiness with 5 complete RSE mock exams. 550 questions with comprehensive rationales.
                  </p>

                  {/* Features */}
                  <div style={{ marginBottom: '20px' }}>
                    {['550 Exam Questions', 'Detailed Rationales', 'Practice & Exam Modes', 'Lifetime Access'].map((feature, fIdx) => (
                      <div key={fIdx} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        marginBottom: '10px'
                      }}>
                        <CheckCircle size={16} color="#D4AF37" />
                        <span style={{ fontSize: '0.875rem', color: '#334155' }}>{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Pricing */}
                  <div style={{
                    background: 'rgba(212,175,55,0.08)',
                    borderRadius: '12px',
                    padding: '16px',
                    marginBottom: '20px',
                    textAlign: 'center'
                  }}>
                    <div style={{
                      fontSize: '2.25rem',
                      fontWeight: '900',
                      color: '#B8860B',
                      marginBottom: '2px',
                      lineHeight: 1
                    }}>
                      $49.99
                    </div>
                    <div style={{
                      fontSize: '0.8rem',
                      fontWeight: '600',
                      color: '#B8860B'
                    }}>
                      One-Time Payment
                    </div>
                  </div>

                  {/* CTA Button */}
                  <Link
                    to="/books/rse-mock-exam-pack"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      width: '100%',
                      padding: '14px',
                      background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)',
                      color: 'white',
                      borderRadius: '10px',
                      fontSize: '0.95rem',
                      fontWeight: '700',
                      textDecoration: 'none',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                    onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    View Mock Exam Pack
                    <ArrowRight size={18} />
                  </Link>
                </div>
              </div>

              {/* RSE Element-wise Question Bank */}
              <div style={{
                background: 'white',
                borderRadius: '20px',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(139,92,246,0.12)',
                transition: 'all 0.3s',
                border: '2px solid rgba(139,92,246,0.3)',
                position: 'relative'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 12px 40px rgba(139,92,246,0.25)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(139,92,246,0.12)';
              }}>
                {/* Badge */}
                <div style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  background: 'rgba(139,92,246,0.2)',
                  border: '1px solid rgba(139,92,246,0.5)',
                  borderRadius: '20px',
                  padding: '6px 12px',
                  fontSize: '0.7rem',
                  color: '#8b5cf6',
                  fontWeight: '700',
                  letterSpacing: '0.5px',
                  zIndex: 1
                }}>
                  NEW
                </div>

                {/* Header */}
                <div style={{
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  padding: '32px 24px',
                  textAlign: 'center',
                  position: 'relative'
                }}>
                  <div style={{ width: '80px', height: '80px', margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Library size={56} color="white" />
                  </div>
                  <h3 style={{
                    fontSize: '1.5rem',
                    fontWeight: '800',
                    color: 'white',
                    marginBottom: '8px'
                  }}>
                    Element Question Bank
                  </h3>
                  <p style={{
                    fontSize: '0.875rem',
                    color: 'rgba(255,255,255,0.9)',
                    margin: 0
                  }}>
                    9 Targeted Practice Banks
                  </p>
                </div>

                {/* Content */}
                <div style={{ padding: '28px 24px' }}>
                  <p style={{
                    fontSize: '0.95rem',
                    color: '#475569',
                    lineHeight: '1.6',
                    marginBottom: '20px',
                    minHeight: '72px'
                  }}>
                    Master each RSE element with focused practice questions and detailed rationales.
                  </p>

                  {/* Features */}
                  <div style={{ marginBottom: '20px' }}>
                    {['9 Element Banks', 'Detailed Rationales', 'Practice & Test Modes', 'Lifetime Access'].map((feature, fIdx) => (
                      <div key={fIdx} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        marginBottom: '10px'
                      }}>
                        <CheckCircle size={16} color="#8b5cf6" />
                        <span style={{ fontSize: '0.875rem', color: '#334155' }}>{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Pricing */}
                  <div style={{
                    background: 'rgba(139,92,246,0.08)',
                    borderRadius: '12px',
                    padding: '16px',
                    marginBottom: '20px',
                    textAlign: 'center'
                  }}>
                    <div style={{
                      fontSize: '2.25rem',
                      fontWeight: '900',
                      color: '#7c3aed',
                      marginBottom: '2px',
                      lineHeight: 1
                    }}>
                      $49.99
                    </div>
                    <div style={{
                      fontSize: '0.8rem',
                      fontWeight: '600',
                      color: '#7c3aed'
                    }}>
                      One-Time Payment
                    </div>
                  </div>

                  {/* CTA Button */}
                  <Link
                    to="/books/rse-element-bank"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      width: '100%',
                      padding: '14px',
                      background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                      color: 'white',
                      borderRadius: '10px',
                      fontSize: '0.95rem',
                      fontWeight: '700',
                      textDecoration: 'none',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                    onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    View Question Bank
                    <ArrowRight size={18} />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Section Divider */}
          <div style={{
            textAlign: 'center',
            margin: '80px 0 48px'
          }}>
            <div style={{
              width: '60px',
              height: '4px',
              background: 'linear-gradient(90deg, transparent, #cbd5e1, transparent)',
              margin: '0 auto 32px'
            }} />
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              padding: '8px 20px',
              borderRadius: '100px',
              marginBottom: '16px'
            }}>
              <span style={{ color: 'white', fontSize: '14px', fontWeight: '700', letterSpacing: '0.5px' }}>
                DERIVATIVES EXAM PREP
              </span>
            </div>
            <h2 style={{
              fontSize: 'clamp(1.75rem, 4vw, 2.25rem)',
              fontWeight: '900',
              color: '#0f172a',
              marginBottom: '12px'
            }}>
              Derivatives Study Resources
            </h2>
            <p style={{
              fontSize: '1.05rem',
              color: '#64748b',
              maxWidth: '700px',
              margin: '0 auto'
            }}>
              Master options, futures, and derivatives strategies with our comprehensive study materials
            </p>
          </div>

          {/* Derivatives Resources Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '28px',
            marginBottom: '80px'
          }}>
            {/* Derivatives Interactive Book */}
            {books.filter(book => book.exam === 'Derivatives').map((book, idx) => (
              <div key={idx} style={{
                background: 'white',
                borderRadius: '20px',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                transition: 'all 0.3s',
                border: '1px solid #e2e8f0'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.15)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
              }}>
                {/* Book Card Content */}
                <div style={{
                  background: book.gradient,
                  padding: '48px 32px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '4rem', marginBottom: '16px' }}>{book.icon}</div>
                  <h3 style={{
                    fontSize: '1.75rem',
                    fontWeight: '800',
                    color: 'white',
                    marginBottom: '8px'
                  }}>
                    {book.title}
                  </h3>
                  <p style={{
                    fontSize: '0.938rem',
                    color: 'rgba(255,255,255,0.9)',
                    marginBottom: '24px'
                  }}>
                    {book.elements} Elements + Mock Exam
                  </p>
                </div>

                <div style={{ padding: '32px' }}>
                  <p style={{
                    fontSize: '0.938rem',
                    color: '#475569',
                    marginBottom: '24px',
                    lineHeight: '1.6'
                  }}>
                    {book.description}
                  </p>

                  <div style={{
                    background: book.bgColor,
                    borderRadius: '12px',
                    padding: '20px',
                    marginBottom: '24px',
                    textAlign: 'center'
                  }}>
                    <div style={{
                      fontSize: '0.875rem',
                      color: '#64748b',
                      textDecoration: 'line-through',
                      marginBottom: '4px'
                    }}>
                      ${book.originalPrice} CAD
                    </div>
                    <div style={{
                      fontSize: '2.5rem',
                      fontWeight: '900',
                      color: book.color,
                      marginBottom: '4px'
                    }}>
                      ${book.price}
                    </div>
                    <div style={{
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: book.color
                    }}>
                      April Special
                    </div>
                  </div>

                  <Link
                    to={book.link}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      width: '100%',
                      padding: '14px',
                      background: book.gradient,
                      color: 'white',
                      borderRadius: '10px',
                      fontSize: '1rem',
                      fontWeight: '700',
                      textDecoration: 'none',
                      transition: 'all 0.3s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                    onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    Start Learning
                    <ArrowRight size={18} />
                  </Link>
                </div>
              </div>
            ))}

            {/* Derivatives Mock Exam Pack */}
            <div
              onClick={() => navigate('/books/derivatives-mock-exam-pack')}
              style={{
                background: 'white',
                borderRadius: '20px',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                position: 'relative'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.15)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
              }}>
              {/* New Badge */}
              <div style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                background: 'rgba(212, 175, 55, 0.15)',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(212, 175, 55, 0.3)',
                borderRadius: '20px',
                padding: '6px 12px',
                fontSize: '0.7rem',
                color: '#B8860B',
                fontWeight: '700',
                letterSpacing: '0.5px',
                zIndex: 1
              }}>
                NEW
              </div>

              {/* Header */}
              <div style={{
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                padding: '32px 24px',
                textAlign: 'center'
              }}>
                <div style={{ width: '80px', height: '80px', margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Target size={56} color="white" />
                </div>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '800',
                  color: 'white',
                  marginBottom: '8px'
                }}>
                  Mock Exam Pack
                </h3>
                <p style={{
                  fontSize: '0.875rem',
                  color: 'rgba(255,255,255,0.9)',
                  margin: 0
                }}>
                  5 Full Practice Exams
                </p>
              </div>

              {/* Content */}
              <div style={{ padding: '32px 24px' }}>
                <p style={{
                  fontSize: '0.938rem',
                  color: '#475569',
                  marginBottom: '24px',
                  lineHeight: '1.7'
                }}>
                  Test your readiness with 5 complete Derivatives mock exams. 600 questions with comprehensive rationales.
                </p>

                <div style={{ marginBottom: '24px' }}>
                  {[
                    '600 Exam Questions',
                    'Detailed Rationales',
                    'Practice & Exam Modes',
                    'Lifetime Access'
                  ].map((feature, idx) => (
                    <div key={idx} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      marginBottom: '10px'
                    }}>
                      <CheckCircle size={16} color="#8b5cf6" />
                      <span style={{ fontSize: '0.875rem', color: '#334155' }}>{feature}</span>
                    </div>
                  ))}
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  paddingTop: '24px',
                  borderTop: '1px solid #e2e8f0'
                }}>
                  <div>
                    <div style={{
                      fontSize: '2rem',
                      fontWeight: '900',
                      color: '#8b5cf6',
                      lineHeight: '1'
                    }}>
                      $49.99
                    </div>
                    <div style={{
                      fontSize: '0.75rem',
                      color: '#64748b',
                      marginTop: '4px'
                    }}>
                      CAD
                    </div>
                  </div>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    color: 'white',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontSize: '0.938rem',
                    fontWeight: '700'
                  }}>
                    View Details <ArrowRight size={18} />
                  </div>
                </div>
              </div>
            </div>

            {/* Derivatives Element Bank Card */}
            <div
              onClick={() => navigate('/books/derivatives-element-bank')}
              style={{
                background: 'white',
                borderRadius: '20px',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                position: 'relative'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.15)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
              }}>
              {/* New Badge */}
              <div style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                background: 'rgba(212, 175, 55, 0.15)',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(212, 175, 55, 0.3)',
                borderRadius: '20px',
                padding: '6px 12px',
                fontSize: '0.7rem',
                color: '#B8860B',
                fontWeight: '700',
                letterSpacing: '0.5px',
                zIndex: 1
              }}>
                NEW
              </div>

              {/* Header */}
              <div style={{
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                padding: '32px 24px',
                textAlign: 'center'
              }}>
                <div style={{ width: '80px', height: '80px', margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Library size={56} color="white" />
                </div>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '800',
                  color: 'white',
                  marginBottom: '8px'
                }}>
                  Element Question Bank
                </h3>
                <p style={{
                  fontSize: '0.875rem',
                  color: 'rgba(255,255,255,0.9)',
                  margin: 0
                }}>
                  8 Element Banks
                </p>
              </div>

              {/* Content */}
              <div style={{ padding: '32px 24px' }}>
                <p style={{
                  fontSize: '0.938rem',
                  color: '#475569',
                  marginBottom: '24px',
                  lineHeight: '1.7'
                }}>
                  Master each topic with 800 targeted practice questions across 8 comprehensive elements
                </p>

                <div style={{ marginBottom: '24px' }}>
                  {[
                    '800 Practice Questions',
                    'Element-wise Organization',
                    'Detailed Explanations',
                    'Lifetime Access'
                  ].map((feature, idx) => (
                    <div key={idx} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      marginBottom: '10px'
                    }}>
                      <CheckCircle size={16} color="#8b5cf6" />
                      <span style={{ fontSize: '0.875rem', color: '#334155' }}>{feature}</span>
                    </div>
                  ))}
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  paddingTop: '24px',
                  borderTop: '1px solid #e2e8f0'
                }}>
                  <div>
                    <div style={{
                      fontSize: '2rem',
                      fontWeight: '900',
                      color: '#8b5cf6',
                      lineHeight: '1'
                    }}>
                      $49.99
                    </div>
                    <div style={{
                      fontSize: '0.75rem',
                      color: '#64748b',
                      marginTop: '4px'
                    }}>
                      CAD
                    </div>
                  </div>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    color: 'white',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontSize: '0.938rem',
                    fontWeight: '700'
                  }}>
                    View Details <ArrowRight size={18} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ISE Section Divider */}
          <div style={{
            textAlign: 'center',
            margin: '80px 0 48px'
          }}>
            <div style={{
              width: '60px',
              height: '4px',
              background: 'linear-gradient(90deg, transparent, #cbd5e1, transparent)',
              margin: '0 auto 32px'
            }} />
            <h2 style={{
              fontSize: 'clamp(1.75rem, 4vw, 2.25rem)',
              fontWeight: '900',
              color: '#0f172a',
              marginBottom: '12px'
            }}>
              ISE Study Resources
            </h2>
            <p style={{
              fontSize: '1.05rem',
              color: '#64748b',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              Master the Institutional Securities Exam with comprehensive interactive modules
            </p>
          </div>

          {/* ISE Exams Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '28px'
          }}>
            {books.filter(book => book.exam === 'ISE').map((book, idx) => (
              <div key={idx} style={{
                background: 'white',
                borderRadius: '20px',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                transition: 'all 0.3s',
                border: '1px solid #e2e8f0'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.15)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
              }}>
                {/* Header */}
                <div style={{
                  background: book.gradient,
                  padding: '32px 24px',
                  textAlign: 'center',
                  position: 'relative'
                }}>
                  <div style={{ width: '80px', height: '80px', margin: '0 auto 16px' }}>{book.icon}</div>
                  <h3 style={{
                    fontSize: '1.5rem',
                    fontWeight: '800',
                    color: 'white',
                    marginBottom: '8px'
                  }}>
                    {book.exam}
                  </h3>
                  <p style={{
                    fontSize: '0.875rem',
                    color: 'rgba(255,255,255,0.9)',
                    margin: 0
                  }}>
                    Interactive Quick Study
                  </p>
                </div>

                {/* Content */}
                <div style={{ padding: '32px 24px' }}>
                  <p style={{
                    fontSize: '0.95rem',
                    color: '#475569',
                    lineHeight: '1.6',
                    marginBottom: '24px'
                  }}>
                    {book.description}
                  </p>

                  {/* Features */}
                  <div style={{ marginBottom: '24px' }}>
                    {book.features.map((feature, fIdx) => (
                      <div key={fIdx} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '12px'
                      }}>
                        <CheckCircle size={18} color={book.color} />
                        <span style={{ fontSize: '0.9rem', color: '#334155' }}>{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Pricing */}
                  <div style={{
                    background: book.bgColor,
                    borderRadius: '12px',
                    padding: '20px',
                    marginBottom: '24px',
                    textAlign: 'center'
                  }}>
                    <div style={{
                      fontSize: '0.875rem',
                      color: '#64748b',
                      textDecoration: 'line-through',
                      marginBottom: '4px'
                    }}>
                      ${book.originalPrice} CAD
                    </div>
                    <div style={{
                      fontSize: '2.5rem',
                      fontWeight: '900',
                      color: book.color,
                      marginBottom: '4px'
                    }}>
                      ${book.price}
                    </div>
                    <div style={{
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: book.color
                    }}>
                      April Special
                    </div>
                  </div>

                  {/* CTA Button */}
                  <Link
                    to={book.link}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      width: '100%',
                      padding: '16px',
                      background: book.gradient,
                      color: 'white',
                      borderRadius: '12px',
                      fontSize: '1rem',
                      fontWeight: '700',
                      textDecoration: 'none',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                    onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    Explore Interactive Guide
                    <ArrowRight size={20} />
                  </Link>
                </div>
              </div>
            ))}

            {/* ISE Mock Exam Pack Tile */}
            <div style={{
              background: 'white',
              borderRadius: '20px',
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              transition: 'all 0.3s',
              border: '1px solid #e2e8f0'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-8px)';
              e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.15)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
            }}>
              {/* Header */}
              <div style={{
                background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)',
                padding: '32px 24px',
                textAlign: 'center',
                position: 'relative'
              }}>
                <div style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  background: '#d4af37',
                  color: '#0f172a',
                  padding: '4px 10px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: '700'
                }}>
                  500 QUESTIONS
                </div>
                <div style={{ width: '80px', height: '80px', margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Target size={48} color="white" />
                </div>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '800',
                  color: 'white',
                  marginBottom: '8px'
                }}>
                  ISE Mock Exams
                </h3>
                <p style={{
                  fontSize: '0.875rem',
                  color: 'rgba(255,255,255,0.9)',
                  margin: 0
                }}>
                  5 Complete Practice Tests
                </p>
              </div>

              {/* Content */}
              <div style={{ padding: '32px 24px' }}>
                <p style={{
                  fontSize: '0.95rem',
                  color: '#475569',
                  lineHeight: '1.6',
                  marginBottom: '24px'
                }}>
                  Prepare for the ISE exam with 5 full-length mock exams covering all 7 elements. Progressive difficulty from foundation to advanced with detailed explanations.
                </p>

                {/* Features */}
                <div style={{ marginBottom: '24px' }}>
                  {['5 Full Mock Exams', '500 Practice Questions', 'Instant Scoring', 'Detailed Explanations'].map((feature, fIdx) => (
                    <div key={fIdx} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '12px'
                    }}>
                      <CheckCircle size={18} color="#1e40af" />
                      <span style={{ fontSize: '0.9rem', color: '#334155' }}>{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Pricing */}
                <div style={{
                  background: '#f1f5f9',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '24px',
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontSize: '0.875rem',
                    color: '#64748b',
                    textDecoration: 'line-through',
                    marginBottom: '4px'
                  }}>
                    $99.99 CAD
                  </div>
                  <div style={{
                    fontSize: '2.5rem',
                    fontWeight: '900',
                    color: '#1e40af',
                    marginBottom: '4px'
                  }}>
                    $49.99
                  </div>
                  <div style={{
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: '#1e40af'
                  }}>
                    One-Time Purchase
                  </div>
                </div>

                {/* CTA Button */}
                <Link
                  to="/books/ise-mock-exam-pack"
                  data-testid="ise-mock-exam-pack-link"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    width: '100%',
                    padding: '16px',
                    background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)',
                    color: 'white',
                    borderRadius: '12px',
                    fontSize: '1rem',
                    fontWeight: '700',
                    textDecoration: 'none',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                  onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  View Mock Exam Pack
                  <ArrowRight size={20} />
                </Link>
              </div>
            </div>

            {/* ISE Element Question Bank Tile */}
            <div style={{
              background: 'white',
              borderRadius: '20px',
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              transition: 'all 0.3s',
              border: '1px solid #e2e8f0'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-8px)';
              e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.15)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
            }}>
              {/* Header */}
              <div style={{
                background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                padding: '32px 24px',
                textAlign: 'center',
                position: 'relative'
              }}>
                <div style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  background: '#d4af37',
                  color: '#0f172a',
                  padding: '4px 10px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: '700'
                }}>
                  700 QUESTIONS
                </div>
                <div style={{ width: '80px', height: '80px', margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <BookOpen size={48} color="white" />
                </div>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '800',
                  color: 'white',
                  marginBottom: '8px'
                }}>
                  ISE Element Question Bank
                </h3>
                <p style={{
                  fontSize: '0.875rem',
                  color: 'rgba(255,255,255,0.9)',
                  margin: 0
                }}>
                  7 Elements - 100 Questions Each
                </p>
              </div>

              {/* Content */}
              <div style={{ padding: '32px 24px' }}>
                <p style={{
                  fontSize: '0.95rem',
                  color: '#475569',
                  lineHeight: '1.6',
                  marginBottom: '24px'
                }}>
                  Master each ISE element independently with 700 targeted practice questions. 100 questions per element with detailed explanations.
                </p>

                {/* Features */}
                <div style={{ marginBottom: '24px' }}>
                  {['7 ISE Elements', '100 Questions/Element', 'Detailed Explanations', 'Topic-Focused Practice'].map((feature, fIdx) => (
                    <div key={fIdx} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '12px'
                    }}>
                      <CheckCircle size={18} color="#7c3aed" />
                      <span style={{ fontSize: '0.9rem', color: '#334155' }}>{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Pricing */}
                <div style={{
                  background: '#f5f3ff',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '24px',
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontSize: '0.875rem',
                    color: '#64748b',
                    textDecoration: 'line-through',
                    marginBottom: '4px'
                  }}>
                    $99.99 CAD
                  </div>
                  <div style={{
                    fontSize: '2.5rem',
                    fontWeight: '900',
                    color: '#7c3aed',
                    marginBottom: '4px'
                  }}>
                    $49.99
                  </div>
                  <div style={{
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: '#7c3aed'
                  }}>
                    One-Time Purchase
                  </div>
                </div>

                {/* CTA Button */}
                <Link
                  to="/books/ise-element-question-bank"
                  data-testid="ise-element-question-bank-link"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    width: '100%',
                    padding: '16px',
                    background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                    color: 'white',
                    borderRadius: '12px',
                    fontSize: '1rem',
                    fontWeight: '700',
                    textDecoration: 'none',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                  onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  View Question Bank
                  <ArrowRight size={20} />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Why Interactive Section */}
        <section style={{
          padding: '80px 24px',
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
        }}>
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            <h2 style={{
              fontSize: 'clamp(2rem, 4vw, 2.5rem)',
              fontWeight: '900',
              color: '#0f172a',
              textAlign: 'center',
              marginBottom: '60px'
            }}>
              Why Choose Interactive Learning?
            </h2>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '40px'
            }}>
              {[
                {
                  title: 'Better Retention',
                  desc: 'Interactive elements help you retain information 70% better than passive reading',
                  stat: '70%',
                  color: '#3b82f6'
                },
                {
                  title: 'Engaging Learning',
                  desc: 'Games and flashcards make studying enjoyable, keeping you motivated throughout',
                  stat: '95%',
                  color: '#22d3ee'
                },
                {
                  title: 'Instant Feedback',
                  desc: 'Know exactly what you need to focus on with immediate results and diagnostics',
                  stat: '100%',
                  color: '#8b5cf6'
                }
              ].map((item, idx) => (
                <div key={idx} style={{
                  background: 'white',
                  borderRadius: '16px',
                  padding: '32px',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontSize: '3rem',
                    fontWeight: '900',
                    color: item.color,
                    marginBottom: '16px'
                  }}>
                    {item.stat}
                  </div>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: '700',
                    color: '#0f172a',
                    marginBottom: '12px'
                  }}>
                    {item.title}
                  </h3>
                  <p style={{
                    fontSize: '0.95rem',
                    color: '#64748b',
                    lineHeight: '1.6',
                    margin: 0
                  }}>
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section style={{
          padding: '80px 24px',
          textAlign: 'center'
        }}>
          <div style={{
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            <h2 style={{
              fontSize: 'clamp(2rem, 4vw, 2.5rem)',
              fontWeight: '900',
              color: '#0f172a',
              marginBottom: '16px'
            }}>
              Ready to Study Smarter?
            </h2>
            <p style={{
              fontSize: '1.125rem',
              color: '#64748b',
              marginBottom: '32px'
            }}>
              Join thousands of successful candidates who passed with our interactive guides
            </p>
            <Link
              to="/books"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '12px',
                padding: '18px 36px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                color: 'white',
                borderRadius: '12px',
                fontSize: '1.125rem',
                fontWeight: '700',
                textDecoration: 'none',
                boxShadow: '0 4px 20px rgba(59, 130, 246, 0.3)',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              View All Study Materials
              <ArrowRight size={24} />
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default InteractiveBooks;
